export const ARRIVAL_WINDOW_END_BY_START: Record<string, string> = {
  "07:30": "08:00",
  "09:30": "11:00",
  "11:30": "13:00",
  "13:30": "15:30"
};

export type ArrivalFlexibilitySettings = {
  enabled: boolean;
  startTime: string;
  endTime: string;
  discountType: "fixed" | "percentage";
  discountValue: number;
  excludedWindowStartTimes: string[];
};

export const defaultArrivalFlexibilitySettings: ArrivalFlexibilitySettings = {
  enabled: false,
  startTime: "07:30",
  endTime: "15:30",
  discountType: "fixed",
  discountValue: 0,
  excludedWindowStartTimes: ["07:30"]
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

export function normalizeArrivalFlexibilityTime(value: unknown) {
  const normalized = String(value ?? "").trim();
  const match = /^(\d{1,2}):(\d{2})$/.exec(normalized);
  if (!match) {
    return "";
  }

  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (!Number.isInteger(hours) || !Number.isInteger(minutes) || hours < 0 || hours > 23 || minutes < 0 || minutes > 59) {
    return "";
  }

  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
}

function normalizeArrivalFlexibilityNumber(value: unknown) {
  const numeric = Number(value ?? 0);
  if (!Number.isFinite(numeric) || numeric <= 0) {
    return 0;
  }
  return numeric;
}

function parseExcludedWindowStartTimes(value: unknown) {
  if (Array.isArray(value)) {
    return value
      .map((entry) => normalizeArrivalFlexibilityTime(entry))
      .filter(Boolean);
  }

  return String(value ?? "")
    .split(/[,\n|]/)
    .map((entry) => normalizeArrivalFlexibilityTime(entry))
    .filter(Boolean);
}

function formatClockLabel(value: string) {
  const normalized = normalizeArrivalFlexibilityTime(value) || String(value ?? "").trim();
  const [rawHours, rawMinutes] = normalized.split(":");
  const hours = Number(rawHours);
  const minutes = Number(rawMinutes);
  if (Number.isNaN(hours) || Number.isNaN(minutes)) {
    return { timeText: normalized, meridiem: "" };
  }

  const meridiem = hours >= 12 ? "PM" : "AM";
  const displayHours = hours % 12 || 12;
  const minuteText = String(minutes).padStart(2, "0");
  return {
    timeText: minuteText === "00" ? String(displayHours) : `${displayHours}:${minuteText}`,
    meridiem
  };
}

export function formatArrivalWindowLabel(startTime: string, endTime: string) {
  const start = formatClockLabel(startTime);
  const end = formatClockLabel(endTime);
  if (start.meridiem && start.meridiem === end.meridiem) {
    return `${start.timeText}-${end.timeText} ${end.meridiem}`;
  }
  return `${start.timeText}${start.meridiem ? ` ${start.meridiem}` : ""}-${end.timeText}${end.meridiem ? ` ${end.meridiem}` : ""}`;
}

export function getArrivalWindowLabelForLocalStart(localStartTime: unknown) {
  const normalizedLocalStart = normalizeArrivalFlexibilityTime(localStartTime);
  if (!normalizedLocalStart) {
    return "";
  }

  const arrivalWindowEnd = ARRIVAL_WINDOW_END_BY_START[normalizedLocalStart];
  if (arrivalWindowEnd) {
    return formatArrivalWindowLabel(normalizedLocalStart, arrivalWindowEnd);
  }

  const fallback = formatClockLabel(normalizedLocalStart);
  return `${fallback.timeText}${fallback.meridiem ? ` ${fallback.meridiem}` : ""}`.trim();
}

export function normalizeArrivalFlexibilitySettings(value: unknown): ArrivalFlexibilitySettings {
  const source = isRecord(value) ? value : {};
  const hasExcludedWindowStartTimes = Object.prototype.hasOwnProperty.call(source, "excludedWindowStartTimes");
  const normalizedExcludedWindowStartTimes = parseExcludedWindowStartTimes(source.excludedWindowStartTimes);

  return {
    enabled: Boolean(source.enabled ?? defaultArrivalFlexibilitySettings.enabled),
    startTime:
      normalizeArrivalFlexibilityTime(source.startTime) || defaultArrivalFlexibilitySettings.startTime,
    endTime:
      normalizeArrivalFlexibilityTime(source.endTime) || defaultArrivalFlexibilitySettings.endTime,
    discountType: source.discountType === "percentage" ? "percentage" : "fixed",
    discountValue: normalizeArrivalFlexibilityNumber(source.discountValue),
    excludedWindowStartTimes: Array.from(
      new Set(
        hasExcludedWindowStartTimes
          ? normalizedExcludedWindowStartTimes
          : defaultArrivalFlexibilitySettings.excludedWindowStartTimes
      )
    )
  };
}

export function getArrivalFlexibilityLabel(value: unknown) {
  const settings = normalizeArrivalFlexibilitySettings(value);
  return formatArrivalWindowLabel(settings.startTime, settings.endTime);
}

export function resolveArrivalFlexibilityOptionState(input: {
  settings: unknown;
  localStartTime: unknown;
  selectedValue?: unknown;
}) {
  const settings = normalizeArrivalFlexibilitySettings(input.settings);
  const localStartTime = normalizeArrivalFlexibilityTime(input.localStartTime);
  const label = getArrivalFlexibilityLabel(settings);
  const eligible =
    settings.enabled &&
    Boolean(localStartTime) &&
    !settings.excludedWindowStartTimes.includes(localStartTime);
  const selected = eligible && Boolean(String(input.selectedValue ?? "").trim());

  return {
    settings,
    localStartTime,
    label,
    eligible,
    selected
  };
}

export function applyArrivalFlexibilityDiscount(input: {
  amountCents: number;
  discountType: "fixed" | "percentage";
  discountValue: number;
}) {
  const amountCents = Math.max(0, Math.round(Number(input.amountCents ?? 0) || 0));
  const discountValue = normalizeArrivalFlexibilityNumber(input.discountValue);
  if (amountCents <= 0 || discountValue <= 0) {
    return 0;
  }

  return input.discountType === "fixed"
    ? Math.min(amountCents, Math.round(discountValue * 100))
    : Math.min(amountCents, Math.round(amountCents * (discountValue / 100)));
}

export function getArrivalFlexibilityDiscountSummary(value: unknown) {
  const settings = normalizeArrivalFlexibilitySettings(value);
  if (settings.discountValue <= 0) {
    return "";
  }

  return settings.discountType === "fixed"
    ? `$${settings.discountValue.toFixed(2)} off`
    : `${settings.discountValue}% off`;
}
